import { Component, computed, effect, input, output, signal } from '@angular/core';

export interface DateRangePreset {
  id: string;
  labelKey: string;
  getRange: () => { start: Date; end: Date };
}

interface MonthView {
  year: number;
  month: number; // 0-11
}

/** Why a manual entry field is currently flagged invalid, if at all. */
type FieldError = 'format' | 'future' | 'order' | null;

interface DayCell {
  date: Date;
  day: number;
  isToday: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  inRange: boolean;
  showRangeStartHalf: boolean;
  showRangeEndHalf: boolean;
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function currentMonthView(): MonthView {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() };
}

/** Default preset ranges shown in the sidebar (Figma: node 79:7025 "Selection List"). */
export const DEFAULT_DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    id: 'today',
    labelKey: 'date_range.today',
    getRange: () => {
      const today = startOfDay(new Date());
      return { start: today, end: today };
    },
  },
  {
    id: 'yesterday',
    labelKey: 'date_range.yesterday',
    getRange: () => {
      const yesterday = startOfDay(new Date());
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: yesterday, end: yesterday };
    },
  },
  {
    id: 'last7',
    labelKey: 'date_range.last_7_days',
    getRange: () => {
      const end = startOfDay(new Date());
      const start = new Date(end);
      start.setDate(start.getDate() - 6);
      return { start, end };
    },
  },
  {
    id: 'last30',
    labelKey: 'date_range.last_30_days',
    getRange: () => {
      const end = startOfDay(new Date());
      const start = new Date(end);
      start.setDate(start.getDate() - 29);
      return { start, end };
    },
  },
  {
    id: 'last3months',
    labelKey: 'date_range.last_3_months',
    getRange: () => {
      const end = startOfDay(new Date());
      const start = new Date(end);
      start.setMonth(start.getMonth() - 3);
      return { start, end };
    },
  },
  {
    id: 'last6months',
    labelKey: 'date_range.last_6_months',
    getRange: () => {
      const end = startOfDay(new Date());
      const start = new Date(end);
      start.setMonth(start.getMonth() - 6);
      return { start, end };
    },
  },
];

/**
 * Reusable custom date-range panel: a sidebar of quick presets, dual (desktop)
 * or single (mobile, via CSS) month calendars with range highlighting, manual
 * dd/mm/yyyy entry, and Reset/Select actions.
 *
 * Figma: desktop node 79:7025, mobile node 649:1977
 * (https://www.figma.com/design/8lgljs3EwYuC7cHy3ps6xU/GivingSpace---Phase-2)
 *
 * This is the selection panel only — the caller owns how/where it's shown
 * (inline, a popover, a dialog, a bottom sheet, etc.) and commits the result
 * only when `rangeSelected` fires; nothing is emitted while the user is still
 * clicking around, so Reset can wipe the in-progress edit back to empty
 * without touching whatever the caller last committed.
 */
@Component({
  selector: 'app-custom-date-range',
  standalone: false,
  templateUrl: './custom-date-range.component.html',
  styleUrl: './custom-date-range.component.scss',
})
export class CustomDateRangeComponent {
  /** Committed start date, set by the caller. */
  startDate = input<Date | null>(null);

  /** Committed end date, set by the caller. */
  endDate = input<Date | null>(null);

  /** Quick-select presets shown in the sidebar (desktop only). */
  presets = input<DateRangePreset[]>(DEFAULT_DATE_RANGE_PRESETS);

  /** When true (default), dates after today cannot be selected. */
  disableFutureDates = input<boolean>(true);

  /** Format used to parse/display the manual entry fields — supports dd, d, MM, M, MMM, yyyy, yy. */
  dateFormat = input<string>('dd/MM/yyyy');

  /** Emits the confirmed range when the user clicks Select. */
  rangeSelected = output<{ start: Date; end: Date }>();

  /** Emits when the user clicks Reset (the draft is wiped to empty first). */
  resetClicked = output<void>();

  readonly DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  readonly MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  draftStart = signal<Date | null>(null);
  draftEnd = signal<Date | null>(null);
  hoverDate = signal<Date | null>(null);
  activePresetId = signal<string | null>(null);
  viewLeft = signal<MonthView>(currentMonthView());
  viewRight = signal<MonthView>(currentMonthView());
  startDateError = signal<FieldError>(null);
  endDateError = signal<FieldError>(null);

  /** Translation key for the Start Date field's current error, or null. */
  startErrorKey = computed(() => this.errorKey('start', this.startDateError()));

  /** Translation key for the End Date field's current error, or null. */
  endErrorKey = computed(() => this.errorKey('end', this.endDateError()));

  // Text actually shown in the manual entry fields. Not a pure computed from
  // draftStart/draftEnd because it must also reflect live, in-progress typing
  // (e.g. "0109" while a full "01/09/2026" hasn't been typed yet) — kept in
  // sync with the draft dates at every other mutation point instead (see
  // syncInputText()).
  startInputValue = signal('');
  endInputValue = signal('');

  leftMonthLabel = computed(() => this.monthLabel(this.viewLeft()));
  rightMonthLabel = computed(() => this.monthLabel(this.viewRight()));

  leftDays = computed(() => this.buildMonth(this.viewLeft()));
  rightDays = computed(() => this.buildMonth(this.viewRight()));

  isLeftNextDisabled = computed(() => this.isNextDisabled(this.viewLeft()));
  isRightNextDisabled = computed(() => this.isNextDisabled(this.viewRight()));

  canConfirm = computed(() => !!this.draftStart() && !!this.draftEnd());

  constructor() {
    // Re-sync the draft (and which months are showing) whenever the caller
    // changes the committed inputs — e.g. an external reset, or an initial
    // value. CRITICAL: only startDate()/endDate()/dateFormat() may be read
    // here (directly or via a called method). This effect *writes*
    // draftStart/draftEnd/startInputValue/endInputValue, so reading any of
    // those back (e.g. via the syncInputText() helper, which reads
    // draftStart()/draftEnd()) would make the effect depend on its own
    // writes — Angular's effect() tracks every signal read during execution
    // regardless of call depth — creating a loop where any later, unrelated
    // write to draftStart/draftEnd (typing a date, clicking a preset, a
    // calendar click) re-triggers this effect and resets them straight back
    // to these (unchanged) caller-provided inputs.
    effect(() => {
      const start = this.startDate();
      const end = this.endDate();
      this.draftStart.set(start);
      this.draftEnd.set(end);
      this.activePresetId.set(null);
      this.viewLeft.set(
        start ? { year: start.getFullYear(), month: start.getMonth() } : currentMonthView()
      );
      this.viewRight.set(
        end ? { year: end.getFullYear(), month: end.getMonth() } : currentMonthView()
      );
      this.startDateError.set(null);
      this.endDateError.set(null);
      this.startInputValue.set(this.formatDate(start));
      this.endInputValue.set(this.formatDate(end));
    });
  }

  selectPreset(preset: DateRangePreset): void {
    const { start, end } = preset.getRange();
    this.draftStart.set(start);
    this.draftEnd.set(end);
    this.activePresetId.set(preset.id);
    this.viewLeft.set({ year: start.getFullYear(), month: start.getMonth() });
    this.viewRight.set({ year: end.getFullYear(), month: end.getMonth() });
    this.startDateError.set(null);
    this.endDateError.set(null);
    this.syncInputText();
  }

  isPresetActive(preset: DateRangePreset): boolean {
    return this.activePresetId() === preset.id;
  }

  isCustomActive(): boolean {
    return this.activePresetId() === 'custom';
  }

  markCustom(): void {
    this.activePresetId.set('custom');
  }

  onDayClick(cell: DayCell): void {
    if (cell.isDisabled) return;
    const start = this.draftStart();
    const end = this.draftEnd();

    if (!start || (start && end)) {
      this.draftStart.set(cell.date);
      this.draftEnd.set(null);
    } else if (cell.date < start) {
      this.draftStart.set(cell.date);
    } else {
      this.draftEnd.set(cell.date);
    }
    this.activePresetId.set('custom');
    this.startDateError.set(null);
    this.endDateError.set(null);
    this.syncInputText();
  }

  onDayHover(date: Date | null): void {
    this.hoverDate.set(date);
  }

  onStartInputChange(value: string): void {
    this.handleTypedInput('start', value);
  }

  onEndInputChange(value: string): void {
    this.handleTypedInput('end', value);
  }

  prevMonth(side: 'left' | 'right'): void {
    this.shiftMonth(side, -1);
  }

  nextMonth(side: 'left' | 'right'): void {
    this.shiftMonth(side, 1);
  }

  /** Wipes the draft back to empty (no dates, no active preset) and notifies the caller. */
  reset(): void {
    this.draftStart.set(null);
    this.draftEnd.set(null);
    this.activePresetId.set(null);
    this.viewLeft.set(currentMonthView());
    this.viewRight.set(currentMonthView());
    this.startDateError.set(null);
    this.endDateError.set(null);
    this.syncInputText();
    this.resetClicked.emit();
  }

  confirm(): void {
    const start = this.draftStart();
    const end = this.draftEnd();
    if (start && end) {
      this.rangeSelected.emit({ start, end });
    }
  }

  /**
   * Live handler for every keystroke in a manual entry field: auto-inserts the
   * `/` separators as digits are typed (e.g. "01092026" -> "01/09/2026") and
   * only attempts full validation once at least 8 digits (dd + MM + yyyy) are
   * in — a partial date is never flagged as invalid while still being typed.
   *
   * Any digits beyond the 8th are kept visible (appended after the masked
   * "dd/mm/yyyy", not silently dropped — e.g. "09092026000" displays as
   * "09/09/2026000") so `parseDate`'s exact-length check correctly rejects
   * it as malformed, instead of quietly truncating to a valid-looking date
   * and accepting extra trailing digits the user actually typed.
   *
   * A non-empty value that contains no digits at all (e.g. "asadasds") can
   * never become a date no matter what's typed next, so it's flagged invalid
   * immediately rather than treated as an in-progress/not-yet-typed value —
   * unlike a value with *some* digits (still under 8), which genuinely could
   * still be mid-typing.
   */
  private handleTypedInput(field: 'start' | 'end', rawValue: string): void {
    const textSignal = field === 'start' ? this.startInputValue : this.endInputValue;
    const errorSignal = field === 'start' ? this.startDateError : this.endDateError;

    const allDigits = rawValue.replace(/\D/g, '');

    if (rawValue.length > 0 && allDigits.length === 0) {
      textSignal.set(rawValue);
      errorSignal.set('format');
      return;
    }

    const masked = this.maskDigits(allDigits) + allDigits.slice(8);
    textSignal.set(masked);

    if (allDigits.length < 8) {
      errorSignal.set(null);
      return;
    }

    this.applyTypedDate(field, masked);
  }

  /** Groups the first 8 raw digits into the fixed dd/MM/yyyy shape `parseDate` expects; ignores anything beyond. */
  private maskDigits(digits: string): string {
    return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
      .filter((part) => part.length > 0)
      .join('/');
  }

  /**
   * Validates a fully-typed date and, if it passes every check, commits it —
   * in priority order: malformed shape, then a disabled (future) date, then
   * range order against whichever of start/end is already set. Each rejection
   * leaves draftStart/draftEnd untouched (the field still shows exactly what
   * was typed, via the mask already applied in handleTypedInput).
   */
  private applyTypedDate(field: 'start' | 'end', value: string): void {
    const errorSignal = field === 'start' ? this.startDateError : this.endDateError;
    const trimmed = value.trim();

    if (!trimmed) {
      errorSignal.set(null);
      return;
    }

    const parsed = this.parseDate(trimmed);
    if (!parsed) {
      errorSignal.set('format');
      return;
    }

    if (this.isDisabled(parsed)) {
      errorSignal.set('future');
      return;
    }

    const other = field === 'start' ? this.draftEnd() : this.draftStart();
    if (field === 'start' && other && parsed > other) {
      errorSignal.set('order');
      return;
    }
    if (field === 'end' && other && parsed < other) {
      errorSignal.set('order');
      return;
    }

    errorSignal.set(null);

    if (field === 'start') {
      this.draftStart.set(parsed);
      this.viewLeft.set({ year: parsed.getFullYear(), month: parsed.getMonth() });
    } else {
      this.draftEnd.set(parsed);
      this.viewRight.set({ year: parsed.getFullYear(), month: parsed.getMonth() });
    }
    this.activePresetId.set('custom');
  }

  /** Maps a field's current error state to its translation key, or null when valid. */
  private errorKey(field: 'start' | 'end', error: FieldError): string | null {
    switch (error) {
      case 'format':
        return 'date_range.invalid_format';
      case 'future':
        return 'date_range.future_date_not_allowed';
      case 'order':
        return field === 'start' ? 'date_range.start_after_end' : 'date_range.end_before_start';
      default:
        return null;
    }
  }

  private syncInputText(): void {
    this.startInputValue.set(this.formatDate(this.draftStart()));
    this.endInputValue.set(this.formatDate(this.draftEnd()));
  }

  private shiftMonth(side: 'left' | 'right', delta: number): void {
    const target = side === 'left' ? this.viewLeft : this.viewRight;
    const current = target();
    let month = current.month + delta;
    let year = current.year;
    if (month > 11) {
      month = 0;
      year++;
    }
    if (month < 0) {
      month = 11;
      year--;
    }
    target.set({ year, month });
  }

  private isNextDisabled(view: MonthView): boolean {
    const now = new Date();
    return (
      view.year > now.getFullYear() ||
      (view.year === now.getFullYear() && view.month >= now.getMonth())
    );
  }

  private buildMonth(view: MonthView): (DayCell | null)[] {
    const { year, month } = view;
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const start = this.draftStart();
    const end = this.draftEnd();
    const hover = this.hoverDate();
    const rawEnd = end ?? hover;
    const effectiveEnd = rawEnd && start && rawEnd > start ? rawEnd : null;

    const cells: (DayCell | null)[] = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isStart = !!start && isSameDay(date, start);
      const isEnd =
        (!!end && isSameDay(date, end)) ||
        (!end && !!effectiveEnd && isSameDay(date, effectiveEnd));
      cells.push({
        date,
        day,
        isToday: isSameDay(date, new Date()),
        isDisabled: this.isDisabled(date),
        isSelected: isStart || isEnd,
        inRange: !!start && !!effectiveEnd && date > start && date < effectiveEnd,
        showRangeStartHalf: isStart && !!(end || effectiveEnd),
        showRangeEndHalf: isEnd,
      });
    }
    return cells;
  }

  private isDisabled(date: Date): boolean {
    if (!this.disableFutureDates()) return false;
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    return date > endOfToday;
  }

  private monthLabel(view: MonthView): string {
    return `${this.MONTHS[view.month]} ${view.year}`;
  }

  private formatDate(date: Date | null): string {
    if (!date) return '';
    const fmt = this.dateFormat();
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return fmt
      .replace(/yyyy/g, String(year))
      .replace(/yy/g, String(year).slice(-2))
      .replace(/MMM/g, '\x01')
      .replace(/MM/g, String(month).padStart(2, '0'))
      .replace(/M(?!M)/g, String(month))
      .replace(/dd/g, String(day).padStart(2, '0'))
      .replace(/d(?!d)/g, String(day))
      .replace(
        /\x01/g,
        ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
          month - 1
        ]
      );
  }

  /** Only the common numeric day/month/year pattern is supported for typed entry. */
  private parseDate(value: string): Date | null {
    const fmt = this.dateFormat().toLowerCase();
    const dayFirst = fmt.indexOf('d') < fmt.indexOf('m');
    const match = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/.exec(value.trim());
    if (!match) return null;

    const [, first, second, yearStr] = match;
    const day = Number(dayFirst ? first : second);
    const month = Number(dayFirst ? second : first);
    const year = Number(yearStr);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day)
      return null;
    return date;
  }
}
